import * as React from 'react';
import styled from 'styled-components';
import { AnimatePresence, motion } from 'framer-motion';
import { AppTitle, AnimatedDiv, MarqueeCarousel } from '../../ui';
import theme from '../../../util/theme';
import { mobileSize, desktopSize } from '../../../util/variables';
import nubankImg from '../../../content/img/homepage/NU.png';
import nubankHeroImg from '../../../content/img/homepage/NU-hero.png';
import figmaImg from '../../../content/img/homepage/Figma.png';
import figmaHeroImg from '../../../content/img/homepage/Figma-hero.png';
import brexImg from '../../../content/img/homepage/Brex.png';
import brexHeroImg from '../../../content/img/homepage/Brex-hero.jpeg';
import happyRobotImg from '../../../content/img/homepage/Happy-robot.png';
import happyRobotHeroImg from '../../../content/img/homepage/Happy-robot-hero.jpg';
import moegoImg from '../../../content/img/homepage/Moego.png';
import moegoHeroImg from '../../../content/img/homepage/moego-hero.png';
import notionImg from '../../../content/img/homepage/Notion.png';
import notionHeroImg from '../../../content/img/homepage/Notion-hero.jpeg';
import wetravelImg from '../../../content/img/homepage/we-travel.png';
import wetravelHeroImg from '../../../content/img/homepage/we-travel-hero.jpg';
import predocImg from '../../../content/img/homepage/Predoc.png';
import predocHeroImg from '../../../content/img/homepage/Predoc-hero.png';
import slideImg from '../../../content/img/homepage/Slide.png';
import slideHeroImg from '../../../content/img/homepage/Slide-hero.png';
import blankStreetImg from '../../../content/img/homepage/blankstreet.png';
import blankStreetHeroImg from '../../../content/img/homepage/blankstreet-hero.jpg';
import lexroomImg from '../../../content/img/homepage/Lexroom.png';
import lexroomHeroImg from '../../../content/img/homepage/Lexroom-hero.png';
import motiveImg from '../../../content/img/homepage/motive.png';
import motiveHeroImg from '../../../content/img/homepage/motive-hero.webp';

import { ArrowUpRight } from '../../../svg/ArrowUpRight';
import { prismicClient, Prismic, RichText } from '../../../services';
import { useEffect, useState } from 'react';
import moment from 'moment';
import { HeroCompanyModal } from '../../Modal';

const companies = [
  {
    slug: 'nubank',
    image: nubankImg,
    heroImage: nubankHeroImg,
  },
  {
    slug: 'figma',
    image: figmaImg,
    heroImage: figmaHeroImg,
  },
  {
    slug: 'brex',
    image: brexImg,
    heroImage: brexHeroImg,
  },
  {
    slug: 'happyrobot',
    image: happyRobotImg,
    heroImage: happyRobotHeroImg,
  },
  {
    slug: 'moego',
    image: moegoImg,
    heroImage: moegoHeroImg,
  },
  {
    slug: 'blank street',
    image: blankStreetImg,
    heroImage: blankStreetHeroImg,
  },
  {
    slug: 'lexroom',
    image: lexroomImg,
    heroImage: lexroomHeroImg,
  },
  {
    slug: 'slide',
    image: slideImg,
    heroImage: slideHeroImg,
  },
  {
    slug: 'wetravel',
    image: wetravelImg,
    heroImage: wetravelHeroImg,
  },
  {
    slug: 'notion',
    image: notionImg,
    heroImage: notionHeroImg,
  },
  {
    slug: 'predoc',
    image: predocImg,
    heroImage: predocHeroImg,
  },
  {
    slug: 'motive',
    image: motiveImg,
    heroImage: motiveHeroImg,
  },
];

const companiesRowOne = companies.slice(0, 6);
const companiesRowTwo = companies.slice(6);

type Company = (typeof companies)[0];

const FADE_TRANSITION = { duration: 1.5, ease: 'easeInOut' };
const ROTATION_INTERVAL_MS = 5000;

const NewSplashScreen: React.FC = React.memo(() => {
  const [showModal, setShowModal] = React.useState(false);
  const [currentCompanyIndex, setCurrentCompanyIndex] = React.useState(0);
  const [rotationEpoch, setRotationEpoch] = React.useState(0);
  const [selectedCompanySlug, setSelectedCompanySlug] = React.useState(companies[0].slug);

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  const handleSelectCompany = (slug: string) => {
    setSelectedCompanySlug(slug);
    toggleModal();
  };

  const handleCompanySelect = (index: number) => {
    setCurrentCompanyIndex(index);
    setRotationEpoch(epoch => epoch + 1);
  };

  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentCompanyIndex(prev => (prev >= companies.length - 1 ? 0 : prev + 1));
    }, ROTATION_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [rotationEpoch]);

  const currentCompany = companies[currentCompanyIndex];

  const renderCompanyCard = (company: Company) => (
    <HeroCompanyCard key={company.slug}>
      <img
        className="image"
        src={company.heroImage}
        alt={company.slug}

      />
      <button type="button" className="button" onClick={() => handleSelectCompany(company.slug)}>
        <ArrowUpRight />
      </button>
      <img className="inverted-logo" src={company.image} alt={company.slug} />
      <div className="bottom-gradient" />
    </HeroCompanyCard>
  );

  const [data, setData] = useState([] as any);

  const company = data?.find(
    (item: any) => RichText.asText(item.name).toLowerCase() === selectedCompanySlug,
  );

  useEffect(() => {
    const fetchData = async () => {
      let allResults: any[] = [];
      let page = 1;
      let hasMorePages = true;

      while (hasMorePages) {
        const response = await prismicClient.query(
          [
            Prismic.Predicates.at('document.type', 'companies'),
            Prismic.Predicates.not('document.tags', ['Archived']),
          ],
          { pageSize: 100, page },
        );

        console.log(`Prismic response for page ${page}:`, response);

        if (response?.results.length) {
          allResults = [...allResults, ...response.results.map((item: any) => item.data)];
        }

        hasMorePages = response.next_page !== null;
        page++;
      }

      setData(allResults);
    };

    fetchData();
  }, []);

  return (
    <HeroContainer>
      <Wrapper>
        <FlexBetweenDiv>
          <LeftDiv>
            <AppTitle>
              <AnimatedDiv delay="100">
                <LargeHeading>
                  Investing in Automation for the{' '}
                  <span style={{ color: theme.PRIMARY_COLOR }}>Real Economy</span>
                </LargeHeading>
              </AnimatedDiv>
            </AppTitle>
            <AnimatedDiv delay="200">
              <Styledh2>
                Serving companies solving problems for the 99%. Donating 50% to the next generation
                of technology founders.
              </Styledh2>
            </AnimatedDiv>
          </LeftDiv>
          <RightDiv>
            <DesktopOnly>
              <CompanyCardStack>
                <AnimatePresence initial={false}>
                  <CompanyCard
                    key={currentCompany.slug}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={FADE_TRANSITION}
                  >
                    <img
                      className="image"
                      src={currentCompany.heroImage}
                      alt={currentCompany.slug}
                    />
                    <button
                      type="button"
                      className="button"
                      onClick={() => handleSelectCompany(currentCompany.slug)}
                    >
                      <ArrowUpRight />
                    </button>
                    <img
                      className="inverted-logo"
                      src={currentCompany.image}
                      alt={currentCompany.slug}
                    />
                    <div className="bottom-gradient" />
                  </CompanyCard>
                </AnimatePresence>
              </CompanyCardStack>
            </DesktopOnly>
            <MobileOnly>
              <MarqueeCarousel
                direction="right"
                duration={50}
                gap={12}
                aria-label="Portfolio companies"
              >
                {companiesRowOne.map(renderCompanyCard)}
              </MarqueeCarousel>

              <MarqueeCarousel
                direction="left"
                duration={50}
                gap={12}
                aria-label="Portfolio companies"
              >
                {companiesRowTwo.map(renderCompanyCard)}
              </MarqueeCarousel>
            </MobileOnly>
          </RightDiv>
        </FlexBetweenDiv>

        <DesktopOnly>
          <div className="companies-container">
            {companies.map((c, index) => (
              <CompanyImageContainer
                key={c.slug}
                type="button"
                active={currentCompanyIndex === index}
                onClick={() => handleCompanySelect(index)}
              >
                <img src={c.image} alt={c.slug} />
              </CompanyImageContainer>
            ))}
          </div>
        </DesktopOnly>

        {company && (
          <HeroCompanyModal
            removeOuterClose
            showModal={showModal}
            toggleModal={toggleModal}
            data={{
              id: `${RichText.asText(company.name)}`,
              name: RichText.asText(company.name),
              logo_url: company.logo_url.url,
              hero_image_url: company?.hero_image_url?.url,
              founders: RichText.asText(company?.founders),
              headquarters: company.headquarters,
              founded_date: moment(company.founded_date, 'YYYY-MM-DD').format('YYYY'),
              invested_date: moment(company.invested_date, 'YYYY-MM-DD').format('YYYY'),
              summary_text: company.summary_text,
              about: RichText.asText(company.about),
              website: company.website.url,
              bw_logo_url: company.logo_url.url,
              primary_industry_string: company.primary_industry,
              primary_trend_string: company.primary_trend,
              primary_trend: {
                name: company.primary_trend,
              },
              primary_industry: {
                name: company.primary_industry,
              },
              business_model: company.business_model,
              customer: company.customer,
              geo_focus: company.geo_focus,
              industry_text: company.industry_text,
              trend: [],
              industry: [],
            }}
          />
        )}
      </Wrapper>
    </HeroContainer>
  );
});

const DesktopOnly = styled.div`
  display: block;

  @media ${mobileSize} {
    display: none;
  }
`;

const MobileOnly = styled.div`
  display: none;

  @media ${mobileSize} {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
`;

const Wrapper = styled.div`
  max-width: 1440px;
  margin-right: auto;
  margin-left: auto;
  @media ${mobileSize} {
    margin: 0px;
    margin-top: 0px;
  }

  @media screen and (max-height: 700px) {
    margin: 0px;
    margin-inline: auto;
    margin-top: 100px;
  }
`;

const CompanyCardStack = styled.div`
  position: relative;
  width: 100%;
  max-width: 580px;
  height: 387px;
`;

const CompanyCard = styled(motion.div)`
  width: 100%;
  max-width: 580px;
  height: 387px;
  background-color: ${theme.GREY_300};
  border-radius: 32px;
  padding: 32px;
  overflow: hidden;
  position: absolute;
  top: 0;
  left: 0;

  .image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
  }

  .button {
    position: absolute;
    bottom: 24px;
    right: 24px;
    width: 72px;
    height: 72px;
    background-color: ${theme.PITCH_BLACK};
    border-radius: 72px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 10;
    border: none;
  }

  .inverted-logo {
    position: absolute;
    bottom: 32px;
    left: 32px;
    height: 36px;
    filter: brightness(0) invert(1);
    z-index: 10;
  }

  .bottom-gradient {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(0deg, rgba(0, 0, 0, 0.8) 0%, rgba(87, 199, 133, 0) 30%);
  }
`;

const HeroCompanyCard = styled.div`
  width: 320px;
  height: 240px;
  background-color: ${theme.GREY_300};
  border-radius: 32px;
  padding: 32px;
  overflow: hidden;
  position: relative;
  cursor: pointer;

  .image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
  }

  .button {
    position: absolute;
    bottom: 16px;
    right: 16px;
    width: 48px;
    height: 48px;
    background-color: ${theme.PITCH_BLACK};
    border-radius: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 10;
    border: none;
  }

  .inverted-logo {
    position: absolute;
    bottom: 20px;
    left: 20px;
    height: 24px;
    filter: brightness(0) invert(1);
    z-index: 10;
  }

  .bottom-gradient {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(0deg, rgba(0, 0, 0, 0.8) 0%, rgba(87, 199, 133, 0) 30%);
    cursor: pointer;
  }

  @media ${mobileSize} {
    width: 240px;
    height: 160px;
    border-radius: 20px;

    .button {
      width: 32px;
      height: 32px;

      svg {
        width: 14px;
        height: 14px;
      }
    }

    .inverted-logo {
      height: 16px;
    }
  }
`;

const CompanyImageContainer = styled.button<{ active: boolean }>`
  height: 35px;
  width: 120px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  border: none;
  padding: 0;
  margin: 0;

  img {
    transition: all 0.8s ease-in-out;
    filter: ${props => (props.active ? 'grayscale(0%)' : 'grayscale(100%)')};
    opacity: ${props => (props.active ? 1 : 0.5)};
    height: 100%;
    object-fit: contain;
  }
`;

const FlexBetweenDiv = styled.div`
  display: grid;
  width: 100%;
  max-width: calc(100vw - 100px);
  grid-template-columns: 600fr 465fr;
  grid-gap: 120px;
  align-items: center;
  padding-top: 100px;
  padding-bottom: 50px;
  padding-inline: 24px;
  @media ${desktopSize} {
    grid-gap: 55px;
  }
  @media ${mobileSize} {
    max-width: calc(100vw);
    margin-top: 0px;
    padding: 0px 0px;
    display: flex;
    flex-direction: column;
    grid-gap: 32px;
    padding-bottom: 40px;
  }
`;

const LeftDiv = styled.div`
  width: 100%;
  max-width: 730px;
  @media ${mobileSize} {
    max-width: 100%;
    padding: 0px 32px;
  }
`;

const RightDiv = styled.div`
  width: 100%;
  @media ${mobileSize} {
    max-width: 100%;
    padding-top: 0px;
    padding-bottom: 0px;
    margin-left: -24px;
    margin-right: -24px;
    width: calc(100% + 48px);
  }
`;

const LargeHeading = styled(AppTitle)`
  font-family: ${theme.PRIMARY_FONT};
  font-weight: 700;
  font-size: 65px;
  letter-spacing: -1px;
  line-height: 70px;
  color: ${theme.NEW_BLACK_COLOR};
  margin-bottom: 36px;
  @media ${mobileSize} {
    font-size: 35px;
    line-height: 40px;
    letter-spacing: 0.2px;
    margin-bottom: 16px;
  }
`;

const HeroContainer = styled.div`
  background-color: ${theme.WHITE_COLOR};
  background-size: cover;
  overflow: hidden;
  position: relative;
  min-height: 100vh;
  padding-top: 90px;
  padding-bottom: 60px;
  width: 100%;
  display: flex;
  align-items: center;
  -webkit-transition: all 1500ms ease-in-out;
  -moz-transition: all 1500ms ease-in-out;
  -o-transition: all 1500ms ease-in-out;
  -ms-transition: all 1500ms ease-in-out;
  transition: all 1500ms ease-in-out;

  .companies-container {
    margin-top: 100px;
    display: grid;
    grid-template-columns: repeat(6, minmax(120px, 1fr));
    justify-items: center;
    row-gap: 80px;
    column-gap: 40px;
  }
`;

const Styledh2 = styled.h2`
  font-family: ${theme.PRIMARY_FONT};
  font-weight: 400;
  font-size: 20px;
  line-height: 1.5;
  margin: 0px;
  margin-bottom: 16px;
  margin-right: 160px;
  color: ${theme.PITCH_BLACK};
  @media ${mobileSize} {
    font-size: 20px;
    line-height: 24px;
    margin-right: 0px;
  }
`;

export { NewSplashScreen };
